import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faCheck, faTrash, faHistory } from '@fortawesome/free-solid-svg-icons';
import ActivityModal, { EntityType } from '../detail/tabs/ActivityModal';
import { InsightsService, sharedApiClient } from '../fetcher';
import { CustomDropdown, CustomDatePicker, useAuth, calendarDateFromInstant, calendarDateToEpochMillis } from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import './ReferralModal.css';
import { StatusBadge } from '@medprompt/shared';

const REFERRAL_TYPE_OPTIONS = [
  { id: 'TWO_WEEK_WAIT', value: 'TWO_WEEK_WAIT', name: '2-WEEK WAIT', description: '2-WEEK WAIT' },
  { id: 'URGENT',        value: 'URGENT',        name: 'URGENT',      description: 'URGENT'      },
  { id: 'ROUTINE',       value: 'ROUTINE',       name: 'ROUTINE',     description: 'ROUTINE'     }
];

export const getReferralTypeLabel = (type) => {
  const option = REFERRAL_TYPE_OPTIONS.find(o => o.value === type);
  return option ? option.name : (type || '—');
};

export const getReferralTypeColor = (type) => {
  switch (type) {
    case 'TWO_WEEK_WAIT': return '#fde8e8';
    case 'URGENT':        return '#fff3e0';
    case 'ROUTINE':       return '#e8f5e9';
    default:              return '#f0f0f0';
  }
};

export const getReferralTypeColorFamily = (type) => {
  switch (type) {
    case 'TWO_WEEK_WAIT': return 'red';
    case 'URGENT':        return 'amber';
    case 'ROUTINE':       return 'green';
    default:              return 'gray';
  }
};

// Kept for the CreateReferralModal pill buttons in ReferralViewController
export const getReferralTypeClass = (type) => {
  switch (type) {
    case 'TWO_WEEK_WAIT': return 'referral-type-badge referral-type-two-week-wait';
    case 'URGENT':        return 'referral-type-badge referral-type-urgent';
    case 'ROUTINE':       return 'referral-type-badge referral-type-routine';
    default:              return 'referral-type-badge';
  }
};

const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId;
  return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
};

const formatDateOfBirth = (dob) => {
  if (!dob) return null;
  try {
    return new Date(dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dob; }
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const toDateInputValue = (iso) => calendarDateFromInstant(iso);

const ReferralModal = ({
  isOpen,
  onClose,
  referral,
  onMarkComplete,
  onUpdate,
  onDelete,
  loading = false,
  isReadOnly = false,
  activeUsers = [],
  teams = []
}) => {
  const insightsManager = useMemo(() => new InsightsService(sharedApiClient), []);

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [editedReferralType, setEditedReferralType] = useState('');
  const [editedReferredAt, setEditedReferredAt] = useState('');
  const [referralTypeDropdownOpen, setReferralTypeDropdownOpen] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (referral) {
      setEditedText(referral.text || '');
      setEditedReferralType(referral.referralType || '');
      setEditedReferredAt(toDateInputValue(referral.referredAt));
      setIsEditingText(false);
    }
  }, [referral]);

  useEffect(() => {
    if (isEditingText && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditingText]);

  useEffect(() => {
    if (isOpen && referral?.referralId) {
      insightsManager.recordReferralOpened(referral.referralId).catch(err =>
        console.warn('Failed to record referral opened:', err)
      );
    }
  }, [isOpen, referral?.referralId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !referral) return null;

  const handleTextClick = () => {
    if (!isReadOnly) setIsEditingText(true);
  };

  const handleTextBlur = () => {
    setIsEditingText(false);
    if (editedText !== (referral.text || '') && editedText.trim()) {
      onUpdate({ referralId: referral.referralId, text: editedText });
    }
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditedText(referral.text || '');
      setIsEditingText(false);
    }
  };

  const handleReferralTypeChange = (option) => {
    setReferralTypeDropdownOpen(false);
    setEditedReferralType(option.value);
    onUpdate({ referralId: referral.referralId, referralType: option.value });
  };

  const handleReferredAtChange = (dateStr) => {
    setEditedReferredAt(dateStr);
    if (dateStr) {
      const referredAtEpochMillis = calendarDateToEpochMillis(dateStr);
      if (referredAtEpochMillis != null) {
        onUpdate({ referralId: referral.referralId, referredAt: referredAtEpochMillis });
      }
    }
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-container task-modal-wide modal-animated">
          {/* Header */}
          <div className="modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <StatusBadge
                label={getReferralTypeLabel(editedReferralType)}
                colorFamily={getReferralTypeColorFamily(editedReferralType)}
                size="normal"
              />
              {referral.isComplete && (
                <span className="referral-complete-badge">
                  <FontAwesomeIcon icon={faCheck} /> Complete
                </span>
              )}
              <h2>{isReadOnly ? 'View Referral' : 'Edit Referral'}</h2>
            </div>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="modal-content task-modal-content-horizontal">
            {/* Left Panel — Patient card + Notes */}
            <div className="task-modal-left-panel">
              <div className="patient-card">
                <div className="patient-card-header">
                  <h3>{referral.patient?.name || 'Unknown Patient'}</h3>
                </div>
                <div className="patient-card-details">
                  <div className="detail-row">
                    <span className="detail-label">NHS Number:</span>
                    <span className="detail-value">
                      {formatNhsId(referral.patient?.nhsId) || <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Date of birth:</span>
                    <span className="detail-value">
                      {formatDateOfBirth(referral.patient?.dateOfBirth) || <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Age:</span>
                    <span className="detail-value">
                      {calculateAge(referral.patient?.dateOfBirth) ?? <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Sex:</span>
                    <span className="detail-value">
                      {referral.patient?.sex || <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Address:</span>
                    <span className="detail-value">
                      {referral.patient?.address || <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">
                      {referral.patient?.phoneNumber || <em>not found</em>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">
                      {referral.patient?.email || <em>not found</em>}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes — click to edit, blur to auto-save */}
              <div className="task-description-container">
                <label className="task-description-label">Notes:</label>
                {isEditingText && !isReadOnly ? (
                  <textarea
                    ref={textareaRef}
                    className="task-description-textarea"
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    onBlur={handleTextBlur}
                    onKeyDown={handleTextKeyDown}
                    rows={6}
                    placeholder="Enter referral notes..."
                  />
                ) : (
                  <div
                    className={`task-description-text ${!isReadOnly ? 'task-description-editable' : ''}`}
                    onClick={handleTextClick}
                    title={!isReadOnly ? 'Click to edit' : ''}
                  >
                    {editedText || <em style={{ color: '#999' }}>No notes</em>}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel — Fields + Actions */}
            <div className="task-modal-right-panel">
              {/* Referral Type */}
              <div className="task-modal-field">
                <label className="task-modal-field-label">Referral Type:</label>
                {isReadOnly ? (
                  <StatusBadge
                    label={getReferralTypeLabel(editedReferralType)}
                    colorFamily={getReferralTypeColorFamily(editedReferralType)}
                    size="normal"
                  />
                ) : (
                  <CustomDropdown
                    selected={getReferralTypeLabel(editedReferralType)}
                    options={REFERRAL_TYPE_OPTIONS}
                    isOpen={referralTypeDropdownOpen}
                    toggleDropdown={() => setReferralTypeDropdownOpen(!referralTypeDropdownOpen)}
                    onSelect={handleReferralTypeChange}
                    keyExtractor={(o) => o.value}
                  />
                )}
              </div>

              {/* Date of Referral */}
              <div className="task-modal-field">
                <label className="task-modal-field-label">Date of Referral:</label>
                {isReadOnly ? (
                  <div className="task-modal-field-value">{formatDate(referral.referredAt)}</div>
                ) : (
                  <CustomDatePicker
                    label=""
                    value={editedReferredAt}
                    onChange={handleReferredAtChange}
                  />
                )}
              </div>

              {/* Completed At (read-only display) */}
              {referral.completedAt && (
                <div className="task-modal-field">
                  <label className="task-modal-field-label">Completed:</label>
                  <div className="task-modal-field-value">{formatDate(referral.completedAt)}</div>
                </div>
              )}

              {/* Audit Trail */}
              <button
                className="task-action-button task-preview-document-button"
                onClick={() => setShowActivityModal(true)}
              >
                <FontAwesomeIcon icon={faHistory} />
                <span>Audit Trail</span>
              </button>

              {/* Mark Complete */}
              {!referral.isComplete && !isReadOnly && (
                <button
                  className="task-action-button task-mark-complete-button"
                  onClick={() => onMarkComplete(referral.referralId)}
                  disabled={loading}
                >
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>Mark Complete</span>
                    </>
                  )}
                </button>
              )}

              {/* Delete */}
              {!isReadOnly && (
                <button
                  className="task-action-button task-delete-button"
                  onClick={() => onDelete(referral.referralId)}
                  disabled={loading}
                >
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Delete Referral</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showActivityModal && (
        <ActivityModal
          isOpen={showActivityModal}
          entityType={EntityType.REFERRAL}
          entityId={referral.referralId}
          entity={referral}
          activeUsers={activeUsers}
          teams={teams}
          onClose={() => setShowActivityModal(false)}
        />
      )}
    </>
  );
};

export default ReferralModal;
